import { useState, useEffect, useMemo, useContext } from 'react'
import moment from 'moment'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import Select from 'react-select'

import {
  formatCurrency,
  formatDateTime,
  getTimezoneDate,
  capitalizeString,
  createWorkbook,
  saveWorkbook,
  setHeaders,
  addWorksheet,
  setColumnsAndRows,
  currencyFormat,
  sumCells,
  DEFAULTNUMHEADERS,
  EMPTY_DISPLAY_VALUE,
  checkPermission,
} from '../../utilities/helpers'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import { getHensonFundReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { DateRangeInputWrapper } from '../DateInputWrapper'
import {
  dateTime,
  constants,
  ORGANIZATION_PERMISSION_SLUGS,
  isHensonFundReportEnabled,
} from '@blocktickets/shared-utils'

import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import { NoPermissionsContainer } from '../NoPermissionsContainer'

import '../PaymentListingsV4Wrapper/paymentListingsV4.scss'

const { ORDER_STATUS, ORDER_STATUS_LABEL, PAYMENT_TYPE_LABEL } = constants

const REMOVED_ORDER_STATUS = [
  ORDER_STATUS.COMPLETE_FROM_ASSIGNMENT,
  ORDER_STATUS.COMPLETE_FROM_EXCHANGE,
  ORDER_STATUS.COMPLETE_FROM_GROUP_PURCHASE,
  ORDER_STATUS.COMPLETE_FROM_INVOICE,
  ORDER_STATUS.COMPLETE_FROM_TRANSFER,
  ORDER_STATUS.DRAFT,
  ORDER_STATUS.INCOMPLETE,
  ORDER_STATUS.INVOICE_SENT,
  ORDER_STATUS.PENDING_SEND,
  ORDER_STATUS.FAILED,
  ORDER_STATUS.DUPLICATE_ORDER,
  ORDER_STATUS.DUPLICATE,
]

const ORDER_STATUS_OPTIONS = Object.values(ORDER_STATUS)
  .filter((status) => !REMOVED_ORDER_STATUS.includes(status))
  .map((status) => ({
    value: status,
    label:
      ORDER_STATUS_LABEL[status] || capitalizeString(status.replace(/_/g, ' ')),
  }))

export default function HensonFundReportWrapper({ organization }) {
  const { hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const reportEnabled = isHensonFundReportEnabled(organization?.uuid)
  const hasPermission =
    reportEnabled &&
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_CUSTOM_FEE_REPORT
    )

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [rows, setRows] = useState(null)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [dateFilterType, setDateFilterType] = useState('date_paid')
  const [selectedStatuses, setSelectedStatuses] = useState(
    ORDER_STATUS_OPTIONS.filter((s) =>
      [ORDER_STATUS.COMPLETE, ORDER_STATUS.PLAN].includes(s.value)
    )
  )

  useEffect(() => {
    if (!orgPermissionsReady) return
    hideLoading()
  }, [organization?.uuid, orgPermissionsReady, hideLoading])

  useEffect(() => {
    const fetchData = async () => {
      if (!fromDate || !toDate) return

      setIsLoadingData(true)

      try {
        const tz = dateTime.getBrowserTimezone('UTC')
        const pad = (n) => String(n).padStart(2, '0')
        const ymd = (d) =>
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

        const startLocalIso = `${ymd(fromDate)}T00:00:00.000`
        const endLocalIso = `${ymd(toDate)}T23:59:59.999`

        const fromUtcIso = dateTime.localToUtc(startLocalIso, tz).toISOString()
        const toUtcIso = dateTime.localToUtc(endLocalIso, tz).toISOString()

        const datePaidOpts =
          dateFilterType === 'date_paid'
            ? {
                dateField: dateFilterType,
                datePaidFrom: ymd(fromDate),
                datePaidTo: ymd(toDate),
              }
            : { dateField: dateFilterType }

        const res = await getHensonFundReport(
          organization.uuid,
          fromUtcIso,
          toUtcIso,
          selectedStatuses.map((s) => s.value),
          datePaidOpts
        )

        setRows(res.data || [])
      } catch (error) {
        console.error('Error fetching Henson Fund report:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }
    if (!orgPermissionsReady || !hasPermission) return
    fetchData()
  }, [
    fromDate,
    toDate,
    dateFilterType,
    organization.uuid,
    selectedStatuses,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setFromDate(start)
    setToDate(end)
  }

  const customFeeAmountTotal = useMemo(() => {
    if (!rows?.length) return 0
    return rows.reduce((sum, row) => {
      const n = Number(row.customFeeAmount)
      return sum + (Number.isFinite(n) ? n : 0)
    }, 0)
  }, [rows])

  const downloadReport = async () => {
    try {
      if (!orgPermissionsReady || !hasPermission) return
      if (!rows || rows.length === 0) return

      const dateRange = `${moment(fromDate).format('MMM D, YYYY')} - ${moment(
        toDate
      ).format('MMM D, YYYY')}`
      const dateFilterLabel =
        dateFilterType === 'date_paid' ? 'Date paid' : 'Order date'

      const workbook = createWorkbook()
      const worksheet = addWorksheet('Custom Fee Report', workbook)

      const headerLines = [`Date Range (${dateFilterLabel}): ${dateRange}`]
      setHeaders(
        'Custom Fee Report',
        organization?.name || '',
        worksheet,
        headerLines
      )

      const keys = [
        'orderId',
        'name',
        'address',
        'email',
        'phone',
        'orderDate',
        'paymentType',
        'customFeeName',
        'customFeeAmount',
      ]

      const formatRow = (row) => {
        const zonedDate = getTimezoneDate(
          row.processedAt ?? row.createdAt,
          row.timezone
        )
        const amt = row.customFeeAmount
        const po = row.paymentOption
        return {
          orderId: row.orderId ?? '',
          name: row.name?.trim() || EMPTY_DISPLAY_VALUE,
          address: row.address?.trim() || EMPTY_DISPLAY_VALUE,
          email: row.email?.trim() || EMPTY_DISPLAY_VALUE,
          phone: row.phone?.trim() || EMPTY_DISPLAY_VALUE,
          orderDate: zonedDate
            ? formatDateTime(zonedDate)
            : EMPTY_DISPLAY_VALUE,
          paymentType:
            po != null && po !== ''
              ? PAYMENT_TYPE_LABEL[po] || capitalizeString(String(po))
              : EMPTY_DISPLAY_VALUE,
          customFeeName: row.customFeeName ?? '',
          customFeeAmount:
            amt != null && amt !== '' ? Number(amt) : EMPTY_DISPLAY_VALUE,
        }
      }

      setColumnsAndRows(keys, rows, formatRow, worksheet)

      const headerRows = DEFAULTNUMHEADERS + headerLines.length
      const columnHeaderRowNumber = headerRows
      const headerRow = worksheet.getRow(columnHeaderRowNumber)
      ;[
        'Order id',
        'Name',
        'Address',
        'Email',
        'Phone',
        'Order date',
        'Payment type',
        'Custom fee name',
        'Custom fee amount',
      ].forEach((label, idx) => {
        headerRow.getCell(idx + 1).value = label
      })
      headerRow.commit?.()

      currencyFormat(9, worksheet, headerRows)

      const firstDataRow = headerRows + 1
      const lastDataRow = worksheet.rowCount
      const totalRowNum = lastDataRow + 1
      worksheet.getRow(totalRowNum).font = { bold: true }
      worksheet.getCell(`H${totalRowNum}`).value = 'Total'
      sumCells(
        'I',
        totalRowNum,
        lastDataRow,
        worksheet,
        headerRows,
        firstDataRow
      )
      worksheet.getCell(`I${totalRowNum}`).numFmt =
        '"$"#,##0.00;[Red]-"$"#,##0.00'

      await saveWorkbook(workbook, 'henson fund report')
    } catch (e) {
      console.error('Excel export failed:', e)
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  return (
    <section className="spacer-sm" data-testid="henson-fund-report-section">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Henson Fund Report</h1>
        </div>
        <p className="section-header-desc">
          View Henson Fund custom fee contributions with buyer contact details
          for the selected date range.
        </p>
      </header>

      <div className="payment-listings-v4-filters mb-4">
        <Stack gap={3}>
          <Stack direction="horizontal" gap={3} className="align-items-end">
            <div style={{ width: '400px' }}>
              <Form.Group controlId="hensonFundDateRangeGroup">
                <Form.Label className="uppercase text-body-xs font-weight-bold">
                  Date Range
                </Form.Label>
                <DateRangeInputWrapper
                  id="hensonFundDateRange"
                  startDate={fromDate}
                  endDate={toDate}
                  onChange={handleDateRangeChange}
                  isReadOnly={isLoadingData}
                  size="sm"
                  styles="form-control-height"
                />
              </Form.Group>
            </div>

            <Form.Group
              controlId="hensonFundDateFilterTypeSelect"
              style={{ width: '180px' }}
            >
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Filter by
              </Form.Label>
              <Form.Select
                className="form-control-height"
                value={dateFilterType}
                onChange={(e) => setDateFilterType(e.target.value)}
                disabled={isLoadingData}
              >
                <option value="order_date">Order date</option>
                <option value="date_paid">Date paid</option>
              </Form.Select>
            </Form.Group>
          </Stack>

          <Stack direction="horizontal" gap={3} className="align-items-end">
            <Form.Group
              controlId="hensonFundStatusSelect"
              className="flex-grow-1"
            >
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Order Status
              </Form.Label>
              <Select
                isMulti
                name="statuses"
                options={ORDER_STATUS_OPTIONS}
                className="react-select-container"
                classNamePrefix="react-select"
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                disabled={isLoadingData}
                placeholder="All Statuses"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '47.59px',
                    fontSize: '14px',
                    backgroundColor: '#fcfcfd',
                    borderWidth: '2px',
                    borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                    borderRadius: '12px',
                    '&:hover': {
                      borderColor: state.isFocused ? '#3e8bf7' : '#e6e8ec',
                    },
                    boxShadow: state.isFocused
                      ? '0 0 0 0.25rem rgba(62, 139, 247, 0.25)'
                      : null,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: '45.59px',
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#eef5ff',
                    borderRadius: '20px',
                    padding: '2px 8px',
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#3e8bf7',
                    fontSize: '13px',
                    fontWeight: '500',
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: '#3e8bf7',
                    ':hover': {
                      backgroundColor: 'transparent',
                      color: '#3e8bf7',
                    },
                  }),
                }}
              />
            </Form.Group>

            <ReportRefreshButton
              onRefresh={refresh}
              isRefreshing={isLoadingData}
              disabled={isLoadingData || !fromDate || !toDate}
            />

            <Button
              variant="outline-light"
              className="icon-button form-control-height d-flex align-items-center gap-2 px-4"
              disabled={isLoadingData || !rows || rows.length === 0}
              onClick={downloadReport}
              style={{
                borderRadius: '20px',
                borderColor: '#e6e8ec',
                color: '#121923',
                fontWeight: '600',
              }}
            >
              <span>Excel</span>
            </Button>
          </Stack>
        </Stack>
      </div>

      {!fromDate || !toDate ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>Get started by choosing your filters</p>
            <p className="subtitle subtitle--dark">
              Select a date range from the options above. The report shows one
              row per Henson Fund custom fee on each matching order.
            </p>
          </EmptyContainer>
        </Card>
      ) : isLoadingData ? (
        <LoadingContainer />
      ) : !rows || rows.length === 0 ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>No data</p>
            <p className="subtitle subtitle--dark">
              No Henson Fund custom fees matched these filters. Try a different
              date range or filter selection.
            </p>
          </EmptyContainer>
        </Card>
      ) : (
        <Card body className="card--md mb-4">
          <div className="table-container">
            <div className="table-container--fixed-height--scrollable">
              <Table responsive hover>
                <thead>
                  <tr className="md md--scroll">
                    <th className="text-left uppercase text-body">Order id</th>
                    <th className="text-left uppercase text-body">Name</th>
                    <th className="text-left uppercase text-body">Address</th>
                    <th className="text-left uppercase text-body">Email</th>
                    <th className="text-left uppercase text-body">Phone</th>
                    <th className="text-left uppercase text-body">
                      Order date
                    </th>
                    <th className="text-left uppercase text-body">
                      Payment type
                    </th>
                    <th className="text-left uppercase text-body">
                      Custom fee name
                    </th>
                    <th className="text-left uppercase text-body">
                      Custom fee amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const zonedDate = getTimezoneDate(
                      row.processedAt ?? row.createdAt,
                      row.timezone
                    )
                    return (
                      <tr
                        key={`${row.orderId}-${row.customFeeName}-${i}`}
                        className="md-x md-x--scroll"
                      >
                        <td className="text-left">{row.orderId}</td>
                        <td className="text-left">
                          {row.name?.trim() || EMPTY_DISPLAY_VALUE}
                        </td>
                        <td className="text-left">
                          {row.address?.trim() || EMPTY_DISPLAY_VALUE}
                        </td>
                        <td className="text-left">
                          {row.email?.trim() || EMPTY_DISPLAY_VALUE}
                        </td>
                        <td className="text-left">
                          {row.phone?.trim() || EMPTY_DISPLAY_VALUE}
                        </td>
                        <td className="text-left">
                          {zonedDate
                            ? formatDateTime(zonedDate)
                            : EMPTY_DISPLAY_VALUE}
                        </td>
                        <td className="text-left">
                          {row.paymentOption != null && row.paymentOption !== ''
                            ? PAYMENT_TYPE_LABEL[row.paymentOption] ||
                              capitalizeString(String(row.paymentOption))
                            : EMPTY_DISPLAY_VALUE}
                        </td>
                        <td className="text-left">
                          {row.customFeeName ?? EMPTY_DISPLAY_VALUE}
                        </td>
                        <td className="text-left">
                          {row.customFeeAmount != null &&
                          row.customFeeAmount !== ''
                            ? formatCurrency(row.customFeeAmount)
                            : EMPTY_DISPLAY_VALUE}
                        </td>
                      </tr>
                    )
                  })}
                  <tr className="md-x md-x--scroll total-row light fw-bold">
                    <td className="text-left uppercase text-body-xs">TOTAL</td>
                    <td className="text-left uppercase text-body-xs"></td>
                    <td className="text-left uppercase text-body-xs"></td>
                    <td className="text-left uppercase text-body-xs"></td>
                    <td className="text-left uppercase text-body-xs"></td>
                    <td className="text-left uppercase text-body-xs"></td>
                    <td className="text-left uppercase text-body-xs"></td>
                    <td className="text-left uppercase text-body-xs"></td>
                    <td className="text-left uppercase text-body-xs">
                      {formatCurrency(customFeeAmountTotal)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </Card>
      )}
    </section>
  )
}
